import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { FileDragDrop as DragDrop, FileDragDropProps, useBanner } from '@tq/ui-components';
import { tqc } from '@tq/ui-components/dist/system';

import { BANNER_TIMEOUT } from 'config';
import { UploadedFile } from 'types';
import { FilePreview, UploadingModal } from './components';
import { SetPreviewFiles, useFileUpload, usePreviewFiles } from './hooks';
import { FileInformation } from './components/FileInformation';
import { deleteFileFromStorageApi } from 'api';

export type FileDragDropExtraProps = {
  groupingReference: string | undefined;
  setUploadedFiles?: SetPreviewFiles;
  showFileInfoMessage?: boolean;
};

export const FileDragDrop: React.FC<FileDragDropProps & FileDragDropExtraProps> = ({
  children,
  multiple,
  groupingReference,
  setUploadedFiles,
  showFileInfoMessage = false,
  ...dragDropProps
}) => {
  const { t } = useTranslation('file_upload');
  const banner = useBanner();

  const { previewFiles, setPreviewFiles, concatPreviewFiles } = usePreviewFiles();

  const { files, progress, uploadFile } = useFileUpload({
    onUploadComplete: (uploadedFiles: UploadedFile[]) => {
      if (multiple) {
        concatPreviewFiles(uploadedFiles);
      } else {
        setPreviewFiles(uploadedFiles);
      }
      banner({
        id: 'file-uploaded-banner',
        title: t('file_upload_success', 'Document uploaded successfully'),
        duration: BANNER_TIMEOUT,
      });
    },
    onUploadFailed: () => {
      banner({
        id: 'file-upload-failed-banner',
        title: t('file_upload_error', 'Document upload failed.'),
        description: t(
          'file_upload_error_description',
          'Please check the document size and/or type.'
        ),
        duration: BANNER_TIMEOUT,
        status: 'error',
      });
    },
  });

  const childNodes = children || (
    <>
      <tqc.span display={{ base: 'none', lg: 'initial' }}>
        {t('drag_and_drop_files_or', 'Drag and drop files or ')}
        <tqc.span color={COLOURS.blue[600]} fontWeight='semibold' ml={1.5}>
          {t('browse', 'Browse')}
        </tqc.span>
      </tqc.span>
      <tqc.span display={{ base: 'initial', lg: 'none' }}>
        {t('tap_to_add_files', 'Tap to add files')}
      </tqc.span>{' '}
    </>
  );

  useEffect(() => setUploadedFiles?.(previewFiles), [previewFiles, setUploadedFiles]);

  return (
    <tqc.div mt={5}>
      {previewFiles?.map((file) => (
        <FilePreview
          key={file.uri}
          uploadedFile={file}
          onRemoveFile={(removeFile) =>
            deleteFileFromStorageApi(file.uri).then(() =>
              setPreviewFiles((existingFiles) =>
                existingFiles?.filter((f) => f.uri !== removeFile.uri)
              )
            )
          }
        />
      ))}

      {!!groupingReference && (multiple || !previewFiles?.length) ? (
        <>
          <DragDrop
            icon={<FaCloudUploadAlt />}
            onChange={(file) => uploadFile(file[0], groupingReference, true)}
            {...dragDropProps}
          >
            {childNodes}
          </DragDrop>
          {showFileInfoMessage && <FileInformation />}
        </>
      ) : null}

      <UploadingModal uploadingFiles={files} progress={progress} />
    </tqc.div>
  );
};
